// PhoneInput — controlled phone field with an inline country trigger
// (flag + ISO-2) on the LEFT of the input shell that opens a localized
// country picker. The picker shows a Favorites section (RU/CN/US) at the
// top and remaining countries alphabetized by their localized name in the
// active i18n locale via `Intl.DisplayNames`.
//
// For RU specifically, when the user types an 11-digit national number
// that starts with `7` or `8` followed by `9` (the standard typo: "8"
// prepended out of habit before a +7 9XX mobile), the leading trunk
// digit is silently dropped BEFORE react-international-phone applies its
// 10-digit mask, so the user does not lose the trailing digit they
// typed. Real 10-digit RU/KZ numbers like +78005553535 (toll-free),
// +78121234567 (St Petersburg regional), +77771234567 (Kazakhstan) are
// NOT affected because they fit the mask without overflow.
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  defaultCountries,
  parseCountry,
  usePhoneInput,
  type CountryIso2,
  type ParsedCountry,
} from 'react-international-phone';
import { useFormRowControl } from './FormRow.js';
// D-017 leaves intra-`ui` imports direct; the barrel already re-exports this
// file, so importing it here would close a cycle.
import { Eyebrow } from './LayoutAtoms.js';

const FAVORITES = ['ru', 'cn', 'us'] as const;

/** Opted-in touch target, one expression shared with the `ConfirmModal` footer. */
const TOUCH_TARGET = 'calc(var(--control-h) + var(--s-4))';

export interface PhoneInputProps {
  /** Current E.164 value from the form ("" means unset). */
  value: string;
  /** Emits E.164 string while valid; emits "" while warning is active. */
  onChange: (e164: string) => void;
  invalid?: boolean;
  defaultCountry?: CountryIso2;
  /**
   * Optional `id` for the inner `<input>` so callers wrapping in their own
   * label (`<label htmlFor=...>`) can attach an accessible name.
   * Within {@link FormRow}, the `id` is provided automatically through
   * context.
   */
  id?: string;
  /** Stable hook for tests; applied to the inner `<input>`. */
  testId?: string;
  style?: CSSProperties;
  /**
   * Opt-in touch presentation (#3041 AC-12): a 44 px country trigger in a shell
   * that grows to fit it, and a picker with a 16 px non-shrinking 44 px search,
   * 44 px rows and viewport-bounded sizing. Off by default — every existing
   * caller keeps its density. The caller decides (narrow or coarse pointer),
   * and the flag reaches the picker as a prop because a style scope around this
   * component does not cross its portal.
   */
  touchTargets?: boolean;
}

/**
 * @aiPurpose international phone input that emits a clean E.164 string
 *   to RHF on every keystroke. The only built-in business rule is the
 *   RU 11-digit auto-normalization (see file header) — every other
 *   country flows through `usePhoneInput` unchanged.
 * @aiInvariant `onChange` always receives the library's E.164 output
 *   verbatim; the auto-normalization happens upstream of the library
 *   by rewriting the input event's value, so the library, RHF, and
 *   `phoneSchema` all observe the same clean number.
 * @aiInvariant the forwarded ref points at the native `<input>` AND the
 *   library keeps its own handle on the same node — dropping either breaks
 *   RHF focus-on-invalid-submit or the mask/caret logic respectively.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, invalid, defaultCountry = 'ru', id, testId, style, touchTargets = false },
  ref,
) {
  const field = useFormRowControl({ id, invalid });
  const [focus, setFocus] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { t, i18n } = useTranslation('phoneInput');

  const { phone, inputValue, country, setCountry, handlePhoneValueChange, inputRef } =
    usePhoneInput({
      defaultCountry,
      value,
      forceDialCode: true,
      onChange: (data) => onChange(data.phone),
    });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const normalized = normalizeRuTrunk(e.target.value, country);
      if (normalized !== e.target.value) {
        // Rewrite the event's `target.value` BEFORE passing to the lib,
        // so its mask formats from the corrected digit string. The
        // selection range matters for the lib's diff logic — point it
        // at the end of the new value so the lib sees the change as a
        // forward edit, not a replace.
        const input = e.target;
        input.value = normalized;
        input.setSelectionRange(normalized.length, normalized.length);
      }
      handlePhoneValueChange(e);
    },
    [handlePhoneValueChange, country],
  );

  // WHY: the library owns `inputRef` (mask + caret restoration reads the live
  // node), and a form library hands down its own field ref for focus. A merge
  // callback keeps both pointed at the same element.
  const setInputNode = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLInputElement | null>).current = node;
    },
    [inputRef, ref],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      <div
        data-testid={testId ? `${testId}-shell` : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 'var(--control-h)',
          // The shell has to grow, or the taller trigger would overflow it.
          ...(touchTargets ? { minHeight: TOUCH_TARGET } : {}),
          padding: '0 10px 0 6px',
          borderRadius: 'var(--r-2)',
          border: `1px solid ${
            invalid ? 'var(--status-error-fg)' : focus ? 'var(--accent-1)' : 'var(--border-1)'
          }`,
          background: 'var(--bg-1)',
          boxShadow: focus && !invalid ? `0 0 0 3px var(--accent-ring)` : 'none',
          transition:
            'border-color var(--dur-1) var(--ease-out), box-shadow var(--dur-1) var(--ease-out)',
        }}
      >
        <CountryTrigger
          iso2={country.iso2}
          ariaLabel={t('changeCountry')}
          expanded={pickerOpen}
          touchTargets={touchTargets}
          // WHY: open-only, not toggle. Clicking the trigger while the picker
          // is already open would otherwise race with the picker's
          // outside-mousedown close handler — the mousedown fires the
          // close, then the subsequent click flips state back to true,
          // visually re-opening the dialog. Esc and outside-click handle
          // the close path.
          onClick={() => setPickerOpen(true)}
        />
        <span
          aria-hidden
          style={{
            width: 1,
            height: 18,
            background: 'var(--border-1)',
          }}
        />
        <input
          ref={setInputNode}
          id={field.id}
          aria-describedby={field.describedBy}
          data-testid={testId}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={inputValue}
          onChange={handleChange}
          onFocus={() => setFocus(true)}
          onBlur={() => {
            setFocus(false);
            // On blur: if the underlying `phone` is just the dial code
            // (user typed nothing), emit "" so RHF treats the field as
            // empty rather than partial.
            if (phone === `+${country.dialCode}`) onChange('');
          }}
          aria-invalid={field.invalid ? true : undefined}
          style={{
            border: 0,
            outline: 0,
            background: 'transparent',
            width: '100%',
            fontFamily: 'var(--font-ui)',
            // WHY (#3039): the caller's `style.fontSize` has to reach the
            //   NATIVE input — mobile WebKit auto-zooms a focused field whose
            //   own font is under 16 px, and that zoom disables the narrow
            //   sheet's visual-viewport sizing.
            fontSize: style?.fontSize ?? 'var(--fz-13)',
            color: 'var(--fg-1)',
            fontVariantNumeric: 'tabular-nums',
          }}
        />
      </div>
      {pickerOpen ? (
        <CountryPicker
          locale={i18n.language}
          touchTargets={touchTargets}
          selectedIso2={country.iso2}
          onPick={(iso2) => {
            setCountry(iso2, { focusOnInput: true });
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
});

/**
 * RU 11-digit auto-normalization: when the raw input value (BEFORE the
 * library's mask runs) contains 11 digits after the +7 dial code AND the
 * first two of those digits are `(7|8)9`, drop the leading trunk digit
 * so the library's 10-digit mask sees the user's actual mobile NSN.
 *
 * The function returns the rewritten input string when it applies, and
 * the original `raw` otherwise. A 10-digit value (real toll-free `8800…`,
 * regional `8121…`, Kazakhstan `7771…`) is NOT 11 digits and therefore
 * passes through untouched.
 */
function normalizeRuTrunk(raw: string, country: ParsedCountry): string {
  if (country.iso2 !== 'ru') return raw;
  const head = `+${country.dialCode}`;
  if (!raw.startsWith(head)) return raw;
  const tail = raw.slice(head.length);
  const digits = tail.replace(/\D/g, '');
  if (digits.length !== 11) return raw;
  const first = digits[0];
  const second = digits[1];
  if ((first !== '7' && first !== '8') || second !== '9') return raw;
  return `${head}${digits.slice(1)}`;
}

/** Maps an ISO-2 code (e.g. "ru") to its regional-indicator flag emoji. */
function flagEmoji(iso2: string): string {
  return iso2
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 'A'.charCodeAt(0)))
    .join('');
}

interface CountryTriggerProps {
  iso2: CountryIso2;
  ariaLabel: string;
  expanded: boolean;
  touchTargets: boolean;
  onClick: () => void;
}

function CountryTrigger({ iso2, ariaLabel, expanded, touchTargets, onClick }: CountryTriggerProps) {
  // WHY: include the current ISO-2 in the accessible name. `aria-label`
  // overrides visible text in the AT name calculation, so a bare "Change
  // country" label would hide the currently selected country (`RU`/`CN`
  // /…) from screen-reader users — a regression from the old chip row
  // where the selected code was the button text.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${iso2.toUpperCase()}, ${ariaLabel}`}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: touchTargets ? TOUCH_TARGET : 26,
        padding: '0 6px',
        border: 0,
        borderRadius: 'var(--r-1)',
        background: 'transparent',
        color: 'var(--fg-1)',
        fontSize: 12,
        cursor: 'pointer',
      }}
    >
      <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
        {flagEmoji(iso2)}
      </span>
      <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 500 }}>{iso2.toUpperCase()}</span>
      <span aria-hidden style={{ color: 'var(--fg-3)', fontSize: 10 }}>
        ▾
      </span>
    </button>
  );
}

/**
 * Resolve the localized country name for `iso2` in `locale` via
 * `Intl.DisplayNames`. Returns `undefined` when the runtime lacks
 * `Intl.DisplayNames` or returns no result for the code — callers fall
 * back to `parseCountry(...).name` (the library's English).
 */
function localizedRegionName(locale: string, iso2: string): string | undefined {
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return undefined;
  try {
    const dn = new Intl.DisplayNames([locale], { type: 'region' });
    const name = dn.of(iso2.toUpperCase());
    return name && name.toUpperCase() !== iso2.toUpperCase() ? name : undefined;
  } catch {
    return undefined;
  }
}

interface CountryPickerProps {
  locale: string;
  /** Prop, not inherited CSS: the picker renders in a portal on `document.body`. */
  touchTargets: boolean;
  selectedIso2: CountryIso2;
  onPick: (iso2: CountryIso2) => void;
  onClose: () => void;
}

interface PickerCountry {
  iso2: CountryIso2;
  dialCode: string;
  name: string;
  englishName: string;
}

function CountryPicker({
  locale,
  touchTargets,
  selectedIso2,
  onPick,
  onClose,
}: CountryPickerProps) {
  const { t } = useTranslation('phoneInput');
  const dialogId = useId();
  const [query, setQuery] = useState('');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    // INVARIANT: when the picker is open, Escape and Tab MUST be handled
    // here and not propagate to ancestor Modal/Drawer keydown listeners,
    // otherwise pressing Escape inside the picker would close the parent
    // user form and discard typed values. Capture phase keeps us ahead of
    // the parent's bubble-phase listener; stopPropagation+stopImmediate
    // prevents both bubble and any sibling capture handlers from firing.
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusables = Array.from(
          dialog.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          e.stopPropagation();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          e.stopPropagation();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useLayoutEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (dialogRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  // Build the localized country list once per locale change, then split
  // into Favorites + Rest sorted by localized name.
  const { favorites, rest } = useMemo(() => {
    const all: PickerCountry[] = defaultCountries.map((c) => {
      const parsed = parseCountry(c);
      const localized = localizedRegionName(locale, parsed.iso2);
      return {
        iso2: parsed.iso2,
        dialCode: parsed.dialCode,
        name: localized ?? parsed.name,
        englishName: parsed.name,
      };
    });
    const favSet = new Set<string>(FAVORITES);
    const favoritesList: PickerCountry[] = [];
    for (const iso2 of FAVORITES) {
      const found = all.find((c) => c.iso2 === iso2);
      if (found) favoritesList.push(found);
    }
    const restList = all
      .filter((c) => !favSet.has(c.iso2))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
    return { favorites: favoritesList, rest: restList };
  }, [locale]);

  const filteredFavorites = useMemo(() => filterCountries(favorites, query), [favorites, query]);
  const filteredRest = useMemo(() => filterCountries(rest, query), [rest, query]);

  const empty = filteredFavorites.length === 0 && filteredRest.length === 0;

  const content = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogId}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        ref={dialogRef}
        style={{
          // Opted in, the panel is bounded by the viewport it opened on: a
          // 360 px panel on a 320 px phone has no room for its own gutters.
          width: touchTargets ? 'min(360px, calc(100vw - var(--s-8)))' : 360,
          maxHeight: touchTargets ? 'min(70vh, calc(100dvh - var(--s-8)))' : '70vh',
          background: 'var(--bg-1)',
          border: '1px solid var(--border-1)',
          borderRadius: 'var(--r-2)',
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          boxShadow: 'var(--shadow-2)',
        }}
      >
        <span id={dialogId} style={{ fontSize: 12, color: 'var(--fg-2)' }}>
          {t('label')}
        </span>
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search.placeholder')}
          aria-label={t('search.placeholder')}
          style={{
            height: touchTargets ? TOUCH_TARGET : 'var(--control-h)',
            // WHY flex-shrink: next to a `flex: 1` list the search compresses
            // to a 19 px sliver on a short screen — measured before this opt-in.
            ...(touchTargets ? { flexShrink: 0 } : {}),
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--r-2)',
            padding: '0 10px',
            // 16 px: mobile WebKit auto-zooms a focused field below it.
            fontSize: touchTargets ? 'var(--fz-16)' : 13,
            background: 'var(--bg-1)',
            color: 'var(--fg-1)',
          }}
        />
        <div role="listbox" aria-label={t('label')} style={{ overflow: 'auto', flex: 1 }}>
          {empty ? (
            <div style={{ padding: 12, color: 'var(--fg-2)', fontSize: 12 }}>
              {t('search.noResults')}
            </div>
          ) : (
            <>
              {filteredFavorites.length > 0 ? (
                <CountryGroup
                  heading={t('favorites')}
                  items={filteredFavorites}
                  selectedIso2={selectedIso2}
                  touchTargets={touchTargets}
                  onPick={onPick}
                />
              ) : null}
              {filteredFavorites.length > 0 && filteredRest.length > 0 ? (
                <hr
                  style={{
                    border: 0,
                    borderTop: '1px solid var(--border-1)',
                    margin: '6px 0',
                  }}
                />
              ) : null}
              {filteredRest.length > 0 ? (
                <CountryGroup
                  items={filteredRest}
                  selectedIso2={selectedIso2}
                  touchTargets={touchTargets}
                  onPick={onPick}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function filterCountries(list: PickerCountry[], query: string): PickerCountry[] {
  const q = query.trim().toLowerCase();
  if (q === '') return list;
  const dialQuery = q.replace(/^\+/, '');
  return list.filter((c) => {
    if (c.name.toLowerCase().includes(q)) return true;
    if (c.iso2.toLowerCase().includes(q)) return true;
    if (c.dialCode.includes(dialQuery)) return true;
    return false;
  });
}

interface CountryGroupProps {
  heading?: string;
  items: PickerCountry[];
  selectedIso2: CountryIso2;
  touchTargets: boolean;
  onPick: (iso2: CountryIso2) => void;
}

function CountryGroup({ heading, items, selectedIso2, touchTargets, onPick }: CountryGroupProps) {
  return (
    <div>
      {heading ? <Eyebrow style={{ padding: '4px 10px' }}>{heading}</Eyebrow> : null}
      {items.map((c) => (
        <button
          key={c.iso2}
          type="button"
          role="option"
          aria-selected={c.iso2 === selectedIso2}
          onClick={() => onPick(c.iso2)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            width: '100%',
            padding: '8px 10px',
            ...(touchTargets ? { minHeight: TOUCH_TARGET } : {}),
            border: 0,
            background: c.iso2 === selectedIso2 ? 'var(--bg-3)' : 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: 13,
            color: 'var(--fg-1)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span aria-hidden>{flagEmoji(c.iso2)}</span>
            <span>{c.name}</span>
          </span>
          <span style={{ color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' }}>
            +{c.dialCode}
          </span>
        </button>
      ))}
    </div>
  );
}
